<template>
    <div class="SkillCard h-full overflow-hidden rounded-lg bg-white dark:bg-surface-card shadow">
        <div class="flex h-full flex-col">
            <!-- Skill image -->
            <div class="relative aspect-[5/4] w-full bg-gray-200 dark:bg-subtle">
                <img
                    v-if="skill.coverImg"
                    :src="skill.coverImg"
                    :alt="skill.title"
                    class="size-full object-cover"
                    loading="lazy"
                />
                <div v-else class="flex h-full items-center justify-center bg-primary-50 dark:bg-primary-950/40">
                    <CIcon name="course-48" class="size-16 text-primary-300" />
                </div>
                
                <div class="absolute inset-x-4 bottom-4 space-y-1">
                    <div v-if="badgeMain">
                        <CBadge :type="badgeMainData.color">{{ badgeMainData.text }}</CBadge>
                    </div>
                    <div v-if="sideBadgeText" class="absolute right-2 top-2">
                        <CBadge type="warning">{{ sideBadgeText }}</CBadge>
                    </div>
                </div>
            </div>

            <!-- Content -->
            <div class="flex flex-1 flex-col p-5">
                <!-- Skill name -->
                <h3 class="mb-2 text-lg font-semibold">{{ skill.title }}</h3>

                <!-- Skill type -->
                <div v-if="skillTypeName" class="mb-3 text-sm text-gray-600 dark:text-txt-body">
                    {{ skillTypeName }}
                </div>

                <!-- Start date for events -->
                <div v-if="isEvent && startDate" class="mb-4 flex items-start text-sm text-gray-700 dark:text-txt-body">
                    <IconTextRow icon="calendar" slim class="-ml-1">
                        {{ startDate }}
                    </IconTextRow>
                </div>

                <!-- Location for physical events -->
                <div v-if="isEvent && !skill.isOnline && skill.city" class="mb-4 flex items-start text-sm text-gray-700 dark:text-txt-body">
                    <IconTextRow icon="location-outline" slim class="-ml-1">
                        {{ skill.city }}
                    </IconTextRow>
                </div>

                <!-- Online badge for online events -->
                <div v-if="isEvent && skill.isOnline" class="mb-4 flex items-start text-sm text-gray-700 dark:text-txt-body">
                    <IconTextRow icon="globe" slim class="-ml-1">
                        Online
                    </IconTextRow>
                </div>

                <!-- Topics/Tags -->
                <div v-if="topics && topics.length" class="mb-3 flex flex-wrap gap-1">
                    <CTags
                        :tags="topics.slice(0, 3)"
                        variant="plain"
                    />
                </div>

                <!-- CTA button -->
                <div class="mt-auto pt-3">
                    <NuxtLink
                        :to="{ name: 'vzdelavanie-slug', params: { slug: skill.slug || skill.id } }"
                        class="inline-flex items-center font-medium text-primary-500 hover:text-primary-600 hover:dark:text-primary-300"
                    >
                        Viac informácií
                        <CIcon name="chevron-right" class="ml-1 size-4" />
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import { BadgeMainType } from '#shared/types/Badge'
import { Skill, SkillType } from '#shared/types/Skill'
import { skillTypeDict } from '#shared/types/dictionary'
import { parseDate } from '#shared/utils'
import { useSkills } from '@/composables/skills/useSkills'
import IconTextRow from '@/components/common/IconTextRow.vue'
import CIcon from '@/components/ui/CIcon.vue'
import CBadge from '@/components/ui/CBadge.vue'
import CTags from '@/components/ui/CTags.vue'

const { formatSkillDate } = useSkills()

interface Props {
    skill: Skill
    returnRoute?: Record<string, any>
    searchTagRouteName?: string
    searchTagQueryParam?: string
    loading?: boolean
    badgeMain?: boolean
    preview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    returnRoute: () => ({}),
    searchTagRouteName: 'vzdelavanie',
    searchTagQueryParam: 'topics',
    loading: false,
    badgeMain: false,
    preview: false
})

const badgeMainData = computed<BadgeMainType>(() => ({
    text: 'Vzdelávanie',
    color: 'primary'
}))

const startDate = computed(() => {
    if (!props.skill.startDate) return null
    return formatSkillDate(props.skill)
})

const topics = computed(() => props.skill?.topics || [])

// Skill type helpers
const isEvent = computed(() => 
    props.skill.type === SkillType.COURSE || 
    props.skill.type === SkillType.WORKSHOP ||
    props.skill.type === SkillType.CONFERENCE
)

const isWorkgroup = computed(() => props.skill.type === SkillType.WORKGROUP)

const skillTypeName = computed(() => {
    if (!props.skill.type) return ''
    return skillTypeDict[props.skill.type as SkillType] || ''
})

const sideBadgeText = computed(() => {
    if (isWorkgroup.value) return 'Prihlás sa'
    return ''
})
</script>